/**
 * MatrixViewTabs Component
 * View tabs with filtering, ordering, and visibility controls
 */

import { useState, useRef, useCallback, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  DndContext,
  closestCenter,
  useSensor,
  useSensors,
  PointerSensor
} from '@dnd-kit/core';
import {
  SortableContext,
  horizontalListSortingStrategy,
  verticalListSortingStrategy,
  arrayMove
} from '@dnd-kit/sortable';
import {
  ChevronRight,
  ChevronDown,
  Plus,
  EyeOff,
  Filter,
  Search,
  X,
  CheckCircle,
  Edit2,
  Trash2,
  Copy,
  Globe,
  Lock
} from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';

import SortableTab from './SortableTab';
import SortableFieldItem from './SortableFieldItem';

const MatrixViewTabs = memo(({
  // View state
  views,
  setViews,
  activeViewId,
  setActiveViewId,
  savingStatus,
  hasUnsavedChanges,

  // Filter state
  activeFilters,
  setActiveFilters,
  toggleFilter,

  // Visibility state
  hiddenColumns,
  setHiddenColumns,
  hiddenRows,
  setHiddenRows,
  columnOrder,
  setColumnOrder,
  rowOrder,
  setRowOrder,

  // Data
  qualifications,
  teamMembers,
  filteredQualifications,
  filteredTeamMembers,

  // User
  userProfile,
  projectId,

  // Handlers
  onRenameView,
  onDeleteView,
  onDuplicateView,
  onToggleViewPublic
}) => {
  // Local state
  const [viewContextMenu, setViewContextMenu] = useState(null);
  const [editingTabId, setEditingTabId] = useState(null);
  const [hideFieldsOpen, setHideFieldsOpen] = useState(false);
  const [hideFieldsTab, setHideFieldsTab] = useState('qualifications');
  const [hideFieldsSearch, setHideFieldsSearch] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [activeTabId, setActiveTabId] = useState(null);
  const [activeFieldDragId, setActiveFieldDragId] = useState(null);
  const [showTabScrollLeft, setShowTabScrollLeft] = useState(false);
  const [showTabScrollRight, setShowTabScrollRight] = useState(false);

  const tabScrollRef = useRef(null);

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  // Handle tab drag end
  const handleTabDragEnd = useCallback((event) => {
    const { active, over } = event;
    setActiveTabId(null);

    if (active.id !== over?.id) {
      const oldIndex = views.findIndex(v => v.id === active.id);
      const newIndex = views.findIndex(v => v.id === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        const newViews = arrayMove(views, oldIndex, newIndex);
        setViews(newViews);

        // Save order to database (optional)
        // This could be moved to a separate handler
      }
    }
  }, [views, setViews]);

  // Create new view
  const handleCreateView = useCallback(async (withFilters = false) => {
    if (!userProfile?.id) {
      toast.error('User not authenticated');
      return;
    }

    try {
      const { data: newView, error } = await supabase
        .from('matrix_views')
        .insert({
          user_id: userProfile.id,
          project_id: projectId,
          name: `View ${views.length}`,
          filters: withFilters ? {
            activeFilters: Array.from(activeFilters),
            hiddenColumns: Array.from(hiddenColumns),
            hiddenRows: Array.from(hiddenRows)
          } : {},
          is_public: false
        })
        .select()
        .single();

      if (error) throw error;

      await supabase
        .from('matrix_view_preferences')
        .insert({
          user_id: userProfile.id,
          view_id: newView.id,
          display_order: views.length,
          is_visible: true
        });

      setViews([...views, {
        id: newView.id,
        name: newView.name,
        filters: newView.filters || {},
        isPublic: false,
        userId: userProfile.id,
        isDefault: false
      }]);
      setActiveViewId(newView.id);

      // Immediately open for editing
      setTimeout(() => {
        setEditingTabId(newView.id);
      }, 100);

      if (withFilters) {
        toast.success('View saved successfully');
      }
    } catch (error) {
      console.error('Error creating view:', error);
      toast.error('Failed to create view');
    }
  }, [userProfile, projectId, views, activeFilters, hiddenColumns, hiddenRows, setViews, setActiveViewId]);

  // Export matrix to CSV
  const handleExportCSV = useCallback(() => {
    try {
      const view = views.find(v => v.id === activeViewId);
      const viewName = view?.name || 'All';

      // Create CSV header row
      const headers = ['Employee', ...filteredQualifications.map(q => q.name)];

      // Create CSV data rows
      const rows = filteredTeamMembers.map(member => {
        const row = [member.name];

        filteredQualifications.forEach(qual => {
          const req = member.requirements?.find(r => r.id === qual.id);
          if (req) {
            let cellValue = req.status;
            if (req.date) {
              const dateStr = new Date(req.date).toLocaleDateString();
              cellValue += ` (${dateStr})`;
            }
            row.push(cellValue);
          } else {
            row.push('');
          }
        });

        return row;
      });

      // Combine headers and rows
      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => {
          if (typeof cell === 'string' && (cell.includes(',') || cell.includes('"'))) {
            return `"${cell.replace(/"/g, '""')}"`;
          }
          return cell;
        }).join(','))
      ].join('\n');

      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `training-matrix-${viewName.toLowerCase().replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success('Matrix exported successfully');
    } catch (error) {
      console.error('Error exporting matrix:', error);
      toast.error('Failed to export matrix');
    }
  }, [views, activeViewId, filteredQualifications, filteredTeamMembers]);

  return (
    <div className="sticky top-0 left-0 z-40 bg-gray-100 pt-1.5 px-4 pb-0">
      <div className="flex items-end gap-4 relative h-[40px]">
        {/* Border line - positioned absolutely below tabs */}
        <div className="absolute bottom-0 left-0 right-0 h-[1px] bg-gray-200 z-0" />

        {/* Left: Folder Tabs - Scrollable Container */}
        <div className="flex-1 min-w-0 flex items-end gap-1">
          {/* Tab Scroll Left Arrow */}
          <button
            onClick={() => {
              if (tabScrollRef.current && showTabScrollLeft) {
                tabScrollRef.current.scrollBy({ left: -200, behavior: 'smooth' });
              }
            }}
            disabled={!showTabScrollLeft}
            className={`flex-shrink-0 w-7 h-7 mb-0.5 flex items-center justify-center rounded transition-all ${
              showTabScrollLeft
                ? 'opacity-100 hover:bg-gray-200/60 cursor-pointer'
                : 'opacity-0 pointer-events-none'
            }`}
            title={showTabScrollLeft ? "Scroll left" : ""}
          >
            <ChevronRight className="h-4 w-4 text-gray-600 rotate-180" />
          </button>

          {/* Tabs Scrollable Area */}
          <div
            ref={tabScrollRef}
            className="flex-1 min-w-0 overflow-x-auto overflow-y-hidden hide-scrollbar"
            onScroll={(e) => {
              const { scrollLeft, scrollWidth, clientWidth } = e.currentTarget;
              setShowTabScrollLeft(scrollLeft > 5);
              setShowTabScrollRight(scrollLeft < scrollWidth - clientWidth - 5);
            }}
          >
            <div className="min-w-max">
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={({ active }) => setActiveTabId(active.id)}
                onDragEnd={handleTabDragEnd}
              >
                <SortableContext
                  items={views.filter(v => !v.isDefault).map(v => v.id)}
                  strategy={horizontalListSortingStrategy}
                >
                  <div className="flex items-end gap-0.5">
                    {views.map((view) => (
                      <SortableTab
                        key={view.id}
                        view={view}
                        isActive={activeViewId === view.id}
                        viewContextMenu={viewContextMenu}
                        setActiveViewId={setActiveViewId}
                        setViewContextMenu={setViewContextMenu}
                        editingTabId={editingTabId}
                        setEditingTabId={setEditingTabId}
                        onRename={onRenameView}
                        userProfile={userProfile}
                        onToggleMenu={(e) => {
                          if (viewContextMenu?.viewId === view.id) {
                            setViewContextMenu(null);
                          } else {
                            const rect = e.currentTarget.getBoundingClientRect();
                            setViewContextMenu({
                              viewId: view.id,
                              x: rect.left,
                              y: rect.bottom + 4
                            });
                          }
                        }}
                      />
                    ))}

                    {/* Add View Button */}
                    <button
                      onClick={() => handleCreateView(false)}
                      className="ml-1 w-8 h-8 mb-0.5 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-200/60 rounded-md transition-all duration-200"
                      title="Add new view"
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  </div>
                </SortableContext>
              </DndContext>
            </div>
          </div>

          {/* Tab Scroll Right Arrow */}
          <button
            onClick={() => {
              if (tabScrollRef.current && showTabScrollRight) {
                tabScrollRef.current.scrollBy({ left: 200, behavior: 'smooth' });
              }
            }}
            disabled={!showTabScrollRight}
            className={`flex-shrink-0 w-7 h-7 mb-0.5 flex items-center justify-center rounded transition-all ${
              showTabScrollRight
                ? 'opacity-100 hover:bg-gray-200/60 cursor-pointer'
                : 'opacity-0 pointer-events-none'
            }`}
            title={showTabScrollRight ? "Scroll right" : ""}
          >
            <ChevronRight className="h-4 w-4 text-gray-600" />
          </button>
        </div>

        {/* Right: Toolbar Buttons */}
        <div className="flex items-center gap-2 flex-shrink-0 self-center">
          {/* Save as New View Button */}
          {hasUnsavedChanges && (
            <button
              onClick={() => handleCreateView(true)}
              className="h-8 px-3 flex items-center gap-2 text-[13px] font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-all duration-200 border border-transparent shadow-sm"
            >
              <Plus className="h-3.5 w-3.5" />
              <span>Save as new view</span>
            </button>
          )}

          {/* Saving Status Indicator */}
          {activeViewId !== 'all' && (() => {
            const currentView = views.find(v => v.id === activeViewId);
            const isOwner = currentView?.userId === userProfile?.id;

            if (!isOwner) return null;

            return (
              <div className="flex items-center gap-1.5 text-[13px] text-gray-500 px-2">
                {savingStatus === 'saving' ? (
                  <>
                    <div className="w-3.5 h-3.5 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
                    <span className="font-medium">Saving...</span>
                  </>
                ) : (
                  <>
                    <svg className="w-3.5 h-3.5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="font-medium">Saved</span>
                  </>
                )}
              </div>
            );
          })()}

          {/* Hide Fields Button */}
          <div className="relative">
            <button
              onClick={() => setHideFieldsOpen(!hideFieldsOpen)}
              className={`h-8 px-3 flex items-center gap-2 text-[13px] font-medium rounded-md transition-all duration-200 ${
                hideFieldsOpen
                  ? 'bg-gray-100 text-gray-900 border border-gray-200'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-white/60 border border-transparent hover:border-gray-200'
              }`}
            >
              <EyeOff className="h-3.5 w-3.5" />
              <span>Hide fields</span>
              <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${hideFieldsOpen ? 'rotate-180' : ''}`} />
            </button>

            {/* Hide Fields Dropdown */}
            {hideFieldsOpen && (
              <>
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setHideFieldsOpen(false)}
                />
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ opacity: { duration: 0.1 }, scale: { duration: 0.1 } }}
                  className="absolute top-full right-0 mt-1 w-80 bg-white rounded-xl shadow-[0_4px_12px_rgba(0,0,0,0.15)] border border-gray-200 z-50 flex flex-col overflow-hidden"
                  style={{ maxHeight: 'min(500px, calc(100vh - 150px))' }}
                >
                  {/* Tab Switcher */}
                  <div className="flex border-b border-gray-200 bg-white flex-shrink-0 rounded-t-xl overflow-hidden relative">
                    <button
                      onClick={() => setHideFieldsTab('qualifications')}
                      className="flex-1 px-4 py-2 text-xs font-medium relative z-10"
                    >
                      <span className={`transition-colors duration-200 ${
                        hideFieldsTab === 'qualifications' ? 'text-blue-600' : 'text-gray-600 hover:text-gray-900'
                      }`}>
                        Qualifications
                      </span>
                    </button>
                    <button
                      onClick={() => setHideFieldsTab('members')}
                      className="flex-1 px-4 py-2 text-xs font-medium relative z-10"
                    >
                      <span className={`transition-colors duration-200 ${
                        hideFieldsTab === 'members' ? 'text-blue-600' : 'text-gray-600 hover:text-gray-900'
                      }`}>
                        Team Members
                      </span>
                    </button>
                    {/* Animated underline */}
                    <motion.div
                      className="absolute bottom-0 h-0.5 bg-blue-600 z-0"
                      initial={false}
                      animate={{
                        x: hideFieldsTab === 'qualifications' ? '0%' : '100%',
                        width: '50%'
                      }}
                      transition={{ type: "spring", stiffness: 300, damping: 30 }}
                    />
                  </div>

                  {/* Search */}
                  <div className="p-3 bg-white flex-shrink-0">
                    <div className="relative">
                      <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Find a field"
                        value={hideFieldsSearch}
                        onChange={(e) => setHideFieldsSearch(e.target.value)}
                        className="w-full pl-8 pr-3 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:border-gray-400 bg-white"
                      />
                      {hideFieldsSearch && (
                        <button
                          className="absolute right-2 top-1/2 transform -translate-y-1/2"
                          onClick={() => setHideFieldsSearch('')}
                        >
                          <X className="h-3.5 w-3.5 text-gray-400 hover:text-gray-600" />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Field List */}
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragStart={({ active }) => setActiveFieldDragId(active.id)}
                    onDragEnd={(event) => {
                      const { active, over } = event;
                      setActiveFieldDragId(null);

                      if (active.id !== over?.id) {
                        if (hideFieldsTab === 'qualifications') {
                          const oldIndex = columnOrder.findIndex(id => id === active.id);
                          const newIndex = columnOrder.findIndex(id => id === over.id);
                          setColumnOrder(arrayMove(columnOrder, oldIndex, newIndex));
                        } else {
                          const oldIndex = rowOrder.findIndex(id => id === active.id);
                          const newIndex = rowOrder.findIndex(id => id === over.id);
                          setRowOrder(arrayMove(rowOrder, oldIndex, newIndex));
                        }
                      }
                    }}
                  >
                    <SortableContext
                      items={hideFieldsTab === 'qualifications' ? columnOrder : rowOrder}
                      strategy={verticalListSortingStrategy}
                    >
                      <div className="overflow-y-auto overflow-x-hidden flex-1 min-h-0 relative">
                        <AnimatePresence mode="wait" initial={false}>
                          {hideFieldsTab === 'qualifications' ? (
                            <motion.div
                              key="qualifications"
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              exit={{ opacity: 0 }}
                              transition={{ duration: 0.15 }}
                            >
                              {columnOrder
                                .map(qualId => qualifications.find(q => q.id === qualId))
                                .filter(qual => {
                                  if (!qual) return false;
                                  if (!hideFieldsSearch) return true;
                                  return qual.name.toLowerCase().includes(hideFieldsSearch.toLowerCase());
                                })
                                .map((qual) => (
                                  <SortableFieldItem
                                    key={qual.id}
                                    item={qual}
                                    isVisible={!hiddenColumns.has(qual.id)}
                                    type="qualification"
                                    onToggle={() => {
                                      const newHidden = new Set(hiddenColumns);
                                      if (!hiddenColumns.has(qual.id)) {
                                        newHidden.add(qual.id);
                                      } else {
                                        newHidden.delete(qual.id);
                                      }
                                      setHiddenColumns(newHidden);
                                    }}
                                  />
                                ))}
                            </motion.div>
                          ) : (
                            <motion.div
                              key="members"
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              exit={{ opacity: 0 }}
                              transition={{ duration: 0.15 }}
                            >
                              {rowOrder
                                .map(memberId => teamMembers.find(m => m.id === memberId))
                                .filter(member => {
                                  if (!member) return false;
                                  if (!hideFieldsSearch) return true;
                                  return member.name.toLowerCase().includes(hideFieldsSearch.toLowerCase());
                                })
                                .map((member) => (
                                  <SortableFieldItem
                                    key={member.id}
                                    item={member}
                                    isVisible={!hiddenRows.has(member.id)}
                                    type="member"
                                    onToggle={() => {
                                      const newHidden = new Set(hiddenRows);
                                      if (!hiddenRows.has(member.id)) {
                                        newHidden.add(member.id);
                                      } else {
                                        newHidden.delete(member.id);
                                      }
                                      setHiddenRows(newHidden);
                                    }}
                                  />
                                ))}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    </SortableContext>
                  </DndContext>

                  {/* Footer */}
                  <div className="px-3 py-2 bg-gray-50 border-t border-gray-200 flex items-center justify-between flex-shrink-0 rounded-b-xl">
                    <button
                      onClick={() => {
                        if (hideFieldsTab === 'qualifications') {
                          setHiddenColumns(new Set());
                        } else {
                          setHiddenRows(new Set());
                        }
                      }}
                      className="text-xs text-gray-600 hover:text-gray-900 font-medium"
                    >
                      Show all
                    </button>
                    <button
                      onClick={() => {
                        if (hideFieldsTab === 'qualifications') {
                          setHiddenColumns(new Set(filteredQualifications.map(q => q.id)));
                        } else {
                          setHiddenRows(new Set(filteredTeamMembers.map(m => m.id)));
                        }
                      }}
                      className="text-xs text-gray-600 hover:text-gray-900 font-medium"
                    >
                      Hide all
                    </button>
                  </div>
                </motion.div>
              </>
            )}
          </div>

          {/* Filters Button */}
          <div className="relative">
            <button
              onClick={() => setFilterOpen(!filterOpen)}
              className={`h-8 px-3 flex items-center gap-2 text-[13px] font-medium rounded-md transition-all duration-200 ${
                filterOpen
                  ? 'bg-gray-100 text-gray-900 border border-gray-200'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-white/60 border border-transparent hover:border-gray-200'
              }`}
            >
              <Filter className="h-3.5 w-3.5" />
              <span>Filter</span>
              {activeFilters.size > 0 && (
                <span className="ml-0.5 px-1.5 py-0.5 bg-blue-500 text-white text-[10px] font-semibold rounded-full">
                  {activeFilters.size}
                </span>
              )}
            </button>

            {/* Filter Dropdown Panel */}
            {filterOpen && (
              <>
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setFilterOpen(false)}
                />
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ opacity: { duration: 0.1 }, scale: { duration: 0.1 } }}
                  className="absolute top-full right-0 mt-1 w-64 bg-white rounded-xl shadow-[0_4px_12px_rgba(0,0,0,0.15)] border border-gray-200 z-50 overflow-hidden"
                >
                  {/* Header */}
                  <div className="px-3 py-2.5 border-b border-gray-200 bg-white flex items-center justify-between">
                    <span className="text-xs font-semibold text-gray-700">Filter by Status</span>
                    {activeFilters.size > 0 && (
                      <button
                        onClick={() => setActiveFilters(new Set())}
                        className="text-[11px] text-blue-600 hover:text-blue-700 font-medium"
                      >
                        Clear all
                      </button>
                    )}
                  </div>

                  {/* Filter Options */}
                  <div className="py-1">
                    {/* Valid */}
                    <FilterOption
                      label="Valid"
                      filter="valid"
                      color="green"
                      activeFilters={activeFilters}
                      toggleFilter={toggleFilter}
                    />

                    {/* Expiring */}
                    <FilterOption
                      label="Expiring"
                      filter="soon"
                      color="yellow"
                      activeFilters={activeFilters}
                      toggleFilter={toggleFilter}
                    />

                    {/* Expired */}
                    <FilterOption
                      label="Expired"
                      filter="expired"
                      color="red"
                      activeFilters={activeFilters}
                      toggleFilter={toggleFilter}
                    />

                    {/* Missing */}
                    <FilterOption
                      label="Missing"
                      filter="missing"
                      color="gray"
                      activeFilters={activeFilters}
                      toggleFilter={toggleFilter}
                    />

                    {/* Divider */}
                    <div className="my-1 border-t border-gray-200" />

                    {/* Assigned */}
                    <FilterOption
                      label="Assigned"
                      filter="assigned"
                      color="blue"
                      activeFilters={activeFilters}
                      toggleFilter={toggleFilter}
                      showDot={false}
                    />

                    {/* Unassigned */}
                    <FilterOption
                      label="Unassigned"
                      filter="unassigned"
                      color="blue"
                      activeFilters={activeFilters}
                      toggleFilter={toggleFilter}
                      showDot={false}
                    />
                  </div>
                </motion.div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* View Context Menu */}
      {viewContextMenu && (
        <ViewContextMenu
          viewContextMenu={viewContextMenu}
          setViewContextMenu={setViewContextMenu}
          views={views}
          userProfile={userProfile}
          setEditingTabId={setEditingTabId}
          onDeleteView={onDeleteView}
          onDuplicateView={onDuplicateView}
          onToggleViewPublic={onToggleViewPublic}
          onExportCSV={handleExportCSV}
        />
      )}
    </div>
  );
});

MatrixViewTabs.displayName = 'MatrixViewTabs';

// Filter option sub-component
const FilterOption = memo(({ label, filter, color, activeFilters, toggleFilter, showDot = true }) => {
  const colorClasses = {
    green: { bg: 'bg-green-500', border: 'border-green-500', dot: 'bg-green-500' },
    yellow: { bg: 'bg-yellow-500', border: 'border-yellow-500', dot: 'bg-yellow-500' },
    red: { bg: 'bg-red-500', border: 'border-red-500', dot: 'bg-red-500' },
    gray: { bg: 'bg-gray-500', border: 'border-gray-500', dot: 'bg-gray-400' },
    blue: { bg: 'bg-blue-500', border: 'border-blue-500', dot: 'bg-blue-500' }
  };

  const isActive = activeFilters.has(filter);
  const classes = colorClasses[color];

  return (
    <button
      onClick={() => toggleFilter(filter)}
      className="w-full px-3 py-2 hover:bg-gray-50 flex items-center justify-between group transition-colors"
    >
      <div className="flex items-center gap-2.5">
        <div className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-all ${
          isActive ? `${classes.bg} ${classes.border}` : 'border-gray-300 group-hover:border-gray-400'
        }`}>
          {isActive && <CheckCircle className="h-3 w-3 text-white" />}
        </div>
        <span className="text-xs text-gray-700">{label}</span>
      </div>
      {showDot && <div className={`w-2 h-2 rounded-full ${classes.dot}`} />}
    </button>
  );
});

FilterOption.displayName = 'FilterOption';

// View context menu sub-component
const ViewContextMenu = memo(({
  viewContextMenu,
  setViewContextMenu,
  views,
  userProfile,
  setEditingTabId,
  onDeleteView,
  onDuplicateView,
  onToggleViewPublic,
  onExportCSV
}) => {
  const currentView = views.find(v => v.id === viewContextMenu.viewId);
  const isOwner = currentView?.userId === userProfile?.id;
  const isDefaultView = currentView?.isDefault || viewContextMenu.viewId === 'all';

  return (
    <>
      <div
        className="fixed inset-0 z-40"
        onClick={() => setViewContextMenu(null)}
      />
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: -10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ duration: 0.15 }}
        className="fixed z-50 bg-white rounded-xl shadow-[0_8px_30px_rgb(0,0,0,0.12)] border border-gray-200/60 py-1.5 min-w-[180px] backdrop-blur-sm"
        style={{
          left: `${viewContextMenu.x}px`,
          top: `${viewContextMenu.y}px`
        }}
      >
        {/* Author name for public views */}
        {!isOwner && !isDefaultView && currentView?.authorName && (
          <div className="px-4 py-2 border-b border-gray-100">
            <p className="text-[11px] text-gray-500">Managed by</p>
            <p className="text-[13px] font-medium text-gray-700">{currentView.authorName}</p>
          </div>
        )}

        {/* Rename */}
        {isOwner && !isDefaultView && (
          <button
            className="w-full px-4 py-2.5 text-[13px] text-left hover:bg-blue-50/80 flex items-center gap-3 text-gray-700 hover:text-blue-700 transition-colors group"
            onClick={() => {
              setEditingTabId(viewContextMenu.viewId);
              setViewContextMenu(null);
            }}
          >
            <Edit2 className="h-4 w-4 text-gray-400 group-hover:text-blue-600 transition-colors" />
            <span className="font-medium">Rename</span>
          </button>
        )}

        {/* Export */}
        <button
          className="w-full px-4 py-2.5 text-[13px] text-left hover:bg-blue-50/80 flex items-center gap-3 text-gray-700 hover:text-blue-700 transition-colors group"
          onClick={() => {
            onExportCSV();
            setViewContextMenu(null);
          }}
        >
          <svg className="h-4 w-4 text-gray-400 group-hover:text-blue-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
          <span className="font-medium">Export as CSV</span>
        </button>

        {/* Duplicate */}
        {!isDefaultView && (
          <button
            className="w-full px-4 py-2.5 text-[13px] text-left hover:bg-blue-50/80 flex items-center gap-3 text-gray-700 hover:text-blue-700 transition-colors group"
            onClick={() => {
              onDuplicateView(viewContextMenu.viewId);
              setViewContextMenu(null);
            }}
          >
            <Copy className="h-4 w-4 text-gray-400 group-hover:text-blue-600 transition-colors" />
            <span className="font-medium">Duplicate</span>
          </button>
        )}

        {/* Toggle Public/Private */}
        {isOwner && !isDefaultView && (
          <button
            className="w-full px-4 py-2.5 text-[13px] text-left hover:bg-blue-50/80 flex items-center gap-3 text-gray-700 hover:text-blue-700 transition-colors group"
            onClick={() => {
              onToggleViewPublic(viewContextMenu.viewId);
              setViewContextMenu(null);
            }}
          >
            {currentView?.isPublic ? (
              <>
                <Lock className="h-4 w-4 text-gray-400 group-hover:text-blue-600 transition-colors" />
                <span className="font-medium">Make Private</span>
              </>
            ) : (
              <>
                <Globe className="h-4 w-4 text-gray-400 group-hover:text-blue-600 transition-colors" />
                <span className="font-medium">Make Public</span>
              </>
            )}
          </button>
        )}

        {/* Delete */}
        {isOwner && !isDefaultView && (
          <>
            <div className="my-1 border-t border-gray-100" />
            <button
              className="w-full px-4 py-2.5 text-[13px] text-left hover:bg-red-50/80 flex items-center gap-3 text-red-600 transition-colors group"
              onClick={() => {
                onDeleteView(viewContextMenu.viewId);
                setViewContextMenu(null);
              }}
            >
              <Trash2 className="h-4 w-4 text-red-400 group-hover:text-red-600 transition-colors" />
              <span className="font-medium">Delete</span>
            </button>
          </>
        )}
      </motion.div>
    </>
  );
});

ViewContextMenu.displayName = 'ViewContextMenu';

export default MatrixViewTabs;
